<template>
  <div class="orders-page">
    <AppHeader />

    <div class="page-body">
      <!-- 页面标题 -->
      <div class="page-heading">
        <div class="page-title">{{ $t("marketOrdersPage.pageTitle") }}</div>
        <div class="page-desc">{{ $t("marketOrdersPage.pageDesc") }}</div>
      </div>

      <!-- 未登录提示 -->
      <div v-if="!isLoggedIn" class="section-card">
        <div class="section-head">
          <div>
            <div class="section-title">{{ $t("marketOrdersPage.loginTitle") }}</div>
            <div class="section-desc">{{ $t("marketOrdersPage.loginDesc") }}</div>
          </div>
          <div class="section-badge">{{ $t("marketOrdersPage.loginBadge") }}</div>
        </div>
      </div>

      <!-- 已登录但加载失败 -->
      <div v-if="isLoggedIn && errorMsg && !loadingStat && !loadingList && !orders.length" class="section-card">
        <div class="section-head">
          <div>
            <div class="section-title">{{ $t("marketOrdersPage.loadFailTitle") }}</div>
            <div class="section-desc">{{ errorMsg }}</div>
          </div>
          <div class="section-badge error">{{ $t("marketOrdersPage.errorBadge") }}</div>
        </div>

        <div class="filter-row" style="margin-top: 16px;">
          <div class="filter-chip active" @click="reloadAll">{{ $t("marketOrdersPage.reload") }}</div>
        </div>
      </div>

      <!-- 订单总览 -->
      <div class="overview-card">
        <div class="overview-bg overview-grid"></div>
        <div class="overview-bg overview-orb overview-orb-1"></div>
        <div class="overview-bg overview-orb overview-orb-2"></div>

        <div class="overview-top">
          <div class="overview-label">{{ $t("marketOrdersPage.overviewLabel") }}</div>
          <div class="overview-total">
            {{ loadingStat && !statLoaded ? '--' : orderStats.totalCount }} {{ $t("marketOrdersPage.ordersUnit") }}
          </div>
        </div>

        <div class="overview-main">
          <div class="overview-main-item">
            <span>{{ $t("marketOrdersPage.runningCount") }}</span>
            <strong>{{ loadingStat && !statLoaded ? '--' : orderStats.runningCount }}</strong>
          </div>
          <div class="overview-main-item">
            <span>{{ $t("marketOrdersPage.finishedCount") }}</span>
            <strong>{{ loadingStat && !statLoaded ? '--' : orderStats.finishedCount }}</strong>
          </div>
          <div class="overview-main-item">
            <span>{{ $t("marketOrdersPage.totalAmount") }}</span>
            <strong>{{ loadingStat && !statLoaded ? '--' : formatMoney(orderStats.totalAmount) }}</strong>
            <em>USDT</em>
          </div>
        </div>

        <div class="overview-bottom">
          <div class="overview-progress-label">
            <span>{{ $t("marketOrdersPage.totalIncome") }}</span>
            <span>
              {{ loadingStat && !statLoaded ? '--' : `${formatMoney(orderStats.totalIncome)} USDT` }}
            </span>
          </div>
          <div class="progress-bar white-track">
            <div
              class="progress-fill white-fill"
              :style="{ width: totalIncomeProgress + '%' }"
            ></div>
          </div>
        </div>
      </div>

      <!-- 状态筛选 -->
      <div class="section-card">
        <div class="section-head">
          <div>
            <div class="section-title">{{ $t("marketOrdersPage.filterTitle") }}</div>
            <div class="section-desc">{{ $t("marketOrdersPage.filterDesc") }}</div>
          </div>
        </div>

        <div class="filter-row">
          <div
            v-for="item in filterTabs"
            :key="item.key"
            class="filter-chip"
            :class="{ active: activeFilter === item.key }"
            @click="changeFilter(item.key)"
          >
            {{ item.label }}
          </div>
        </div>
      </div>

      <!-- 订单列表 -->
      <div class="section-card">
        <div class="section-head">
          <div>
            <div class="section-title">{{ $t("marketOrdersPage.listTitle") }}</div>
            <div class="section-desc">{{ $t("marketOrdersPage.listDesc") }}</div>
          </div>
        </div>

        <div v-if="loadingList && !orders.length" class="empty-box">
          <div class="empty-title">{{ $t("marketOrdersPage.loadingTitle") }}</div>
          <div class="empty-desc">{{ $t("marketOrdersPage.loadingListDesc") }}</div>
        </div>

        <div class="order-list" v-else-if="orders.length">
          <div class="order-card" v-for="item in orders" :key="item.orderNo">
            <div class="order-top">
              <div class="order-no-wrap">
                <div class="order-no-label">{{ $t("marketOrdersPage.orderNoLabel") }}</div>
                <div class="order-no">#{{ item.orderNo }}</div>
              </div>
              <div class="order-status" :class="item.statusClass">
                {{ getOrderStatusText(item) }}
              </div>
            </div>

            <div class="order-main-grid">
              <div class="order-main-item">
                <span>{{ $t("marketOrdersPage.amount") }}</span>
                <strong>{{ formatMoney(item.amount) }} USDT</strong>
              </div>
              <div class="order-main-item">
                <span>{{ $t("marketOrdersPage.currentRate") }}</span>
                <strong>{{ item.rate || '--' }}</strong>
              </div>
              <div class="order-main-item">
                <span>{{ $t("marketOrdersPage.createTime") }}</span>
                <strong>{{ formatDateTime(item.createTime) }}</strong>
              </div>
              <div class="order-main-item">
                <span>{{ $t("marketOrdersPage.orderStatus") }}</span>
                <strong>{{ getOrderStatusText(item) }}</strong>
              </div>
            </div>

            <div class="income-box">
              <div class="income-box-top">
                <div>
                  <div class="income-box-label">{{ $t("marketOrdersPage.accumulatedIncome") }}</div>
                  <div class="income-box-value">{{ formatMoney(item.income) }} USDT</div>
                </div>
                <div class="income-box-side">
                  <span>{{ $t("marketOrdersPage.incomeCap") }}</span>
                  <strong>{{ formatMoney(item.maxIncome) }} USDT</strong>
                </div>
              </div>

              <div class="order-progress-row">
                <span>{{ $t("marketOrdersPage.incomeProgress") }}</span>
                <span>{{ getOrderProgress(item) }}%</span>
              </div>
              <div class="progress-bar">
                <div
                  class="progress-fill blue-fill"
                  :style="{ width: getOrderProgress(item) + '%' }"
                ></div>
              </div>
            </div>

            <div class="order-actions">
              <div class="action-btn ghost" @click="copyOrderNo(item.orderNo)">
                {{ $t("marketOrdersPage.copyOrderNo") }}
              </div>
              <div class="action-btn primary" @click="viewDetail(item)">
                {{ $t("marketOrdersPage.viewDetail") }}
              </div>
            </div>
          </div>

          <div class="load-more-wrap">
            <div v-if="loadingMore" class="load-more-text">{{ $t("marketOrdersPage.loadingMore") }}</div>
            <div v-else-if="hasMore" class="load-more-btn" @click="loadMore">{{ $t("marketOrdersPage.loadMore") }}</div>
            <div v-else class="load-more-text done">{{ $t("marketOrdersPage.noMore") }}</div>
          </div>
        </div>

        <div v-else class="empty-box">
          <div class="empty-title">{{ $t("marketOrdersPage.emptyTitle") }}</div>
          <div class="empty-desc">{{ $t("marketOrdersPage.emptyDesc") }}</div>
        </div>
      </div>

      <!-- 订单详情弹窗 -->
      <transition name="fade">
        <div
          v-if="detailVisible"
          class="detail-mask"
          @click="closeDetail"
        ></div>
      </transition>

      <transition name="sheet-up">
        <div v-if="detailVisible" class="detail-sheet">
          <div class="detail-sheet-handle"></div>

          <div class="detail-sheet-head">
            <div>
              <div class="detail-sheet-title">{{ $t("marketOrdersPage.detailTitle") }}</div>
              <div class="detail-sheet-desc">{{ $t("marketOrdersPage.detailDesc") }}</div>
            </div>
            <div class="detail-close" @click="closeDetail">{{ $t("marketOrdersPage.close") }}</div>
          </div>

          <div v-if="detailLoading" class="empty-box detail-loading-box">
            <div class="empty-title">{{ $t("marketOrdersPage.loadingTitle") }}</div>
            <div class="empty-desc">{{ $t("marketOrdersPage.loadingDetailDesc") }}</div>
          </div>

          <div v-else-if="currentOrder" class="detail-content">
            <div class="detail-status-row">
              <div class="detail-order-no">
                <span>{{ $t("marketOrdersPage.orderNoLabel") }}</span>
                <strong>#{{ currentOrder.orderNo }}</strong>
              </div>
              <div class="order-status" :class="currentOrder.statusClass">
                {{ getOrderStatusText(currentOrder) }}
              </div>
            </div>

            <div class="detail-highlight-card">
              <div class="detail-highlight-main">
                <span>{{ $t("marketOrdersPage.amount") }}</span>
                <strong>{{ formatMoney(currentOrder.amount) }} USDT</strong>
              </div>
              <div class="detail-highlight-side">
                <span>{{ $t("marketOrdersPage.currentRate") }}</span>
                <strong>{{ currentOrder.rate || '--' }}</strong>
              </div>
            </div>

            <div class="detail-grid">
              <div class="detail-item">
                <span>{{ $t("marketOrdersPage.createTime") }}</span>
                <strong>{{ formatDateTime(currentOrder.createTime) }}</strong>
              </div>
              <div class="detail-item">
                <span>{{ $t("marketOrdersPage.orderStatus") }}</span>
                <strong>{{ getOrderStatusText(currentOrder) }}</strong>
              </div>
              <div class="detail-item">
                <span>{{ $t("marketOrdersPage.accumulatedIncome") }}</span>
                <strong>{{ formatMoney(currentOrder.income) }} USDT</strong>
              </div>
              <div class="detail-item">
                <span>{{ $t("marketOrdersPage.incomeCap") }}</span>
                <strong>{{ formatMoney(currentOrder.maxIncome) }} USDT</strong>
              </div>
              <div class="detail-item">
                <span>{{ $t("marketOrdersPage.incomeProgress") }}</span>
                <strong>{{ getOrderProgress(currentOrder) }}%</strong>
              </div>
              <div class="detail-item">
                <span>{{ $t("marketOrdersPage.remainingReward") }}</span>
                <strong>{{ formatMoney(currentOrder.remainingRewardAmount) }} USDT</strong>
              </div>
              <div class="detail-item" v-if="currentOrder.effectiveAt">
                <span>{{ $t("marketOrdersPage.effectiveTime") }}</span>
                <strong>{{ formatDateTime(currentOrder.effectiveAt) }}</strong>
              </div>
              <div class="detail-item" v-if="currentOrder.finishedAt">
                <span>{{ $t("marketOrdersPage.finishedTime") }}</span>
                <strong>{{ formatDateTime(currentOrder.finishedAt) }}</strong>
              </div>
              <div class="detail-item" v-if="currentOrder.lastStaticRewardAt">
                <span>{{ $t("marketOrdersPage.lastStaticRewardTime") }}</span>
                <strong>{{ formatDateTime(currentOrder.lastStaticRewardAt) }}</strong>
              </div>
              <div class="detail-item" v-if="currentOrder.lastRewardAt">
                <span>{{ $t("marketOrdersPage.lastRewardTime") }}</span>
                <strong>{{ formatDateTime(currentOrder.lastRewardAt) }}</strong>
              </div>
            </div>

            <div class="detail-progress-box">
              <div class="order-progress-row">
                <span>{{ $t("marketOrdersPage.currentIncomeProgress") }}</span>
                <span>{{ formatMoney(currentOrder.income) }} / {{ formatMoney(currentOrder.maxIncome) }}</span>
              </div>
              <div class="progress-bar">
                <div
                  class="progress-fill blue-fill"
                  :style="{ width: getOrderProgress(currentOrder) + '%' }"
                ></div>
              </div>
            </div>

            <div class="detail-reward-card">
              <div class="detail-reward-title">{{ $t("marketOrdersPage.rewardBreakdown") }}</div>

              <div class="detail-reward-grid">
                <div class="detail-reward-item">
                  <span>{{ $t("marketOrdersPage.staticReward") }}</span>
                  <strong>{{ formatMoney(currentOrder.staticRewardAmount) }} USDT</strong>
                </div>
                <div class="detail-reward-item">
                  <span>{{ $t("marketOrdersPage.dynamicReward") }}</span>
                  <strong>{{ formatMoney(currentOrder.dynamicRewardAmount) }} USDT</strong>
                </div>
                <div class="detail-reward-item">
                  <span>{{ $t("marketOrdersPage.poolReward") }}</span>
                  <strong>{{ formatMoney(currentOrder.poolRewardAmount) }} USDT</strong>
                </div>
                <div class="detail-reward-item">
                  <span>{{ $t("marketOrdersPage.directReward") }}</span>
                  <strong>{{ formatMoney(currentOrder.directRewardAmount) }} USDT</strong>
                </div>
              </div>
            </div>

            <div v-if="currentOrder.remark" class="detail-remark-card">
              <div class="detail-reward-title">{{ $t("marketOrdersPage.remark") }}</div>
              <div class="detail-remark-text">{{ currentOrder.remark }}</div>
            </div>

            <div class="detail-actions">
              <div class="action-btn ghost" @click="copyOrderNo(currentOrder.orderNo)">
                {{ $t("marketOrdersPage.copyOrderNo") }}
              </div>
              <div class="action-btn primary" @click="closeDetail">
                {{ $t("marketOrdersPage.gotIt") }}
              </div>
            </div>
          </div>
        </div>
      </transition>

      <AppFooter />
    </div>
  </div>
</template>
<script>
import AppHeader from '@/components/AppHeader.vue'
import AppFooter from '@/components/AppFooter.vue'
import wallet from '@/store/wallet'
import {
  fetchGeoOrderStat,
  fetchGeoOrderPage,
  fetchGeoOrderDetail
} from '@/api/geoParticipate'

const statCache = {
  authKey: '',
  data: null,
  inflight: null
}

const pageCacheMap = {}

function readStorageToken() {
  const keys = ['token', 'access_token', 'Access-Token', 'Authorization', 'AUTH_TOKEN']

  for (let i = 0; i < keys.length; i++) {
    const key = keys[i]
    const localVal = window.localStorage.getItem(key)
    if (localVal) return localVal

    const sessionVal = window.sessionStorage.getItem(key)
    if (sessionVal) return sessionVal
  }

  return ''
}

function extractResponseData(res) {
  if (!res) return {}
  if (res.data && typeof res.data === 'object' && !Array.isArray(res.data)) {
    return res.data
  }
  return res
}

function toSafeNumber(val) {
  if (val === null || val === undefined || val === '') return 0
  const n = Number(val)
  return Number.isNaN(n) ? 0 : n
}

export default {
  name: 'MarketOrdersPage',
  components: {
    AppHeader,
    AppFooter
  },
  data() {
    return {
      walletPoller: null,
      lastAddr: '',
      lastConn: null,
      lastAuthKey: '',

      loadingStat: false,
      statLoaded: false,
      loadingList: false,
      loadingMore: false,
      detailLoading: false,
      errorMsg: '',

      activeFilter: 'all',
      detailVisible: false,
      currentOrder: null,

      pageNum: 1,
      pageSize: 10,
      total: 0,
      hasMore: false,

      orderStats: this.createEmptyStat(),
      orders: []
    }
  },
  computed: {
    isLoggedIn() {
      return !!this.getAuthKey()
    },

    currentStatusParam() {
      const map = {
        all: null,
        pending: 0,
        running: 1,
        finished: 2,
        failed: 3
      }
      return map[this.activeFilter]
    },

    totalIncomeProgress() {
      const totalMaxIncome = toSafeNumber(this.orderStats.totalMaxIncome)
      if (!totalMaxIncome) return 0
      return Math.min(
        100,
        Math.round((toSafeNumber(this.orderStats.totalIncome) / totalMaxIncome) * 100)
      )
    },

    filterTabs() {
      return [
        { key: 'all', label: this.$t('marketOrdersPage.filterAll') },
        { key: 'running', label: this.$t('marketOrdersPage.filterRunning') },
        { key: 'finished', label: this.$t('marketOrdersPage.filterFinished') },
        { key: 'pending', label: this.$t('marketOrdersPage.filterPending') },
        { key: 'failed', label: this.$t('marketOrdersPage.filterFailed') }
      ]
    }
  },
  mounted() {
    this.bindEvents()
    this.syncWalletLoginState()

    if (this.isLoggedIn) {
      this.reloadAll()
    } else {
      this.resetPageState()
    }

    this.walletPoller = setInterval(() => {
      const st = wallet?.state || {}
      const addr = st.address || st.account || st.userAddress || ''
      const connected = !!st.connected || !!st.isConnected || (!!addr && addr.length > 0)
      const authKey = this.getAuthKey()

      if (addr !== this.lastAddr || connected !== this.lastConn) {
        this.lastAddr = addr
        this.lastConn = connected
        this.syncWalletLoginState()
      }

      if (authKey !== this.lastAuthKey) {
        this.lastAuthKey = authKey
        this.invalidateAllCache()

        if (authKey) {
          this.reloadAll()
        } else {
          this.resetPageState()
        }
      }
    }, 400)
  },
  activated() {
    if (this.isLoggedIn) {
      this.reloadAll()
    }
  },
  beforeDestroy() {
    if (this.walletPoller) clearInterval(this.walletPoller)
    this.unbindEvents()
    document.body.style.overflow = ''
  },
  methods: {
    createEmptyStat() {
      return {
        totalCount: 0,
        runningCount: 0,
        finishedCount: 0,
        pendingCount: 0,
        invalidCount: 0,
        totalAmount: 0,
        totalIncome: 0,
        totalMaxIncome: 0
      }
    },

    resetPageState() {
      this.loadingStat = false
      this.statLoaded = true
      this.loadingList = false
      this.loadingMore = false
      this.detailLoading = false
      this.errorMsg = ''
      this.orderStats = this.createEmptyStat()
      this.orders = []
      this.pageNum = 1
      this.total = 0
      this.hasMore = false
      this.closeDetail()
    },

    bindEvents() {
      window.addEventListener('auth:login', this.handleAuthLogin)
      window.addEventListener('auth:logout', this.handleAuthLogout)
    },

    unbindEvents() {
      window.removeEventListener('auth:login', this.handleAuthLogin)
      window.removeEventListener('auth:logout', this.handleAuthLogout)
    },

    async handleAuthLogin() {
      this.invalidateAllCache()
      this.syncWalletLoginState()
      await this.reloadAll()
    },

    handleAuthLogout() {
      this.invalidateAllCache()
      this.resetPageState()
    },

    syncWalletLoginState() {
      const st = wallet?.state || {}
      this.lastAddr = st.address || st.account || st.userAddress || ''
      this.lastConn = !!st.connected || !!st.isConnected || (!!this.lastAddr && this.lastAddr.length > 0)
      this.lastAuthKey = this.getAuthKey()
    },

    getWalletAddress() {
      return (
        wallet?.state?.address ||
        wallet?.state?.account ||
        wallet?.state?.walletAddress ||
        wallet?.state?.userAddress ||
        ''
      )
    },

    getAuthKey() {
      const token =
        wallet?.state?.token ||
        wallet?.state?.accessToken ||
        readStorageToken()

      const address = this.getWalletAddress()
      return [token, address].filter(Boolean).join('|')
    },

    getPageCacheKey(filterKey, pageSize) {
      const authKey = this.getAuthKey()
      return `${authKey}::${filterKey}::${pageSize}`
    },

    invalidateAllCache() {
      statCache.authKey = ''
      statCache.data = null
      statCache.inflight = null

      Object.keys(pageCacheMap).forEach((key) => {
        delete pageCacheMap[key]
      })
    },

    normalizeStatData(raw) {
      const data = raw || {}
      return {
        totalCount: toSafeNumber(data.totalCount),
        runningCount: toSafeNumber(data.runningCount),
        finishedCount: toSafeNumber(data.finishedCount),
        pendingCount: toSafeNumber(data.pendingCount),
        invalidCount: toSafeNumber(data.invalidCount),
        totalAmount: toSafeNumber(data.totalAmount),
        totalIncome: toSafeNumber(data.totalIncome),
        totalMaxIncome: toSafeNumber(data.totalMaxIncome)
      }
    },

    normalizeOrderItem(item) {
      return {
        orderNo: item?.orderNo || '',
        amount: toSafeNumber(item?.amount),
        rate: item?.rate || '',
        income: toSafeNumber(item?.income),
        maxIncome: toSafeNumber(item?.maxIncome),
        createTime: item?.createTime || '',
        status: item?.status,
        statusText: item?.statusText || '',
        statusClass: item?.statusClass || this.mapStatusClass(item?.status),
        progress: toSafeNumber(item?.progress),
        remainingRewardAmount: toSafeNumber(item?.remainingRewardAmount),
        staticRewardAmount: toSafeNumber(item?.staticRewardAmount),
        dynamicRewardAmount: toSafeNumber(item?.dynamicRewardAmount),
        poolRewardAmount: toSafeNumber(item?.poolRewardAmount),
        directRewardAmount: toSafeNumber(item?.directRewardAmount),
        effectiveAt: item?.effectiveAt || '',
        finishedAt: item?.finishedAt || '',
        lastStaticRewardAt: item?.lastStaticRewardAt || '',
        lastRewardAt: item?.lastRewardAt || '',
        remark: item?.remark || ''
      }
    },

    normalizePageData(raw) {
      const data = raw || {}
      const records = Array.isArray(data.records) ? data.records.map(this.normalizeOrderItem) : []
      const total = toSafeNumber(data.total)
      const current = toSafeNumber(data.current || data.pageNum || this.pageNum)
      const size = toSafeNumber(data.size || data.pageSize || this.pageSize)

      return {
        records,
        total,
        current,
        size
      }
    },

    normalizeDetailData(raw, fallbackItem = null) {
      const item = this.normalizeOrderItem(raw || {})
      if (fallbackItem) {
        return {
          ...fallbackItem,
          ...item,
          orderNo: item.orderNo || fallbackItem.orderNo,
          amount: item.amount || fallbackItem.amount,
          income: item.income || fallbackItem.income,
          maxIncome: item.maxIncome || fallbackItem.maxIncome,
          rate: item.rate || fallbackItem.rate,
          statusText: item.statusText || fallbackItem.statusText,
          statusClass: item.statusClass || fallbackItem.statusClass
        }
      }
      return item
    },

    mapStatusClass(status) {
      switch (Number(status)) {
        case 0: return 'pending'
        case 1: return 'running'
        case 2: return 'finished'
        case 3: return 'failed'
        default: return 'finished'
      }
    },

    getOrderStatusText(item) {
      const status = Number(item?.status)
      const statusClass = String(item?.statusClass || '').toLowerCase()

      if (status === 0 || statusClass === 'pending') return this.$t('marketOrdersPage.statusPending')
      if (status === 1 || statusClass === 'running') return this.$t('marketOrdersPage.statusRunning')
      if (status === 2 || statusClass === 'finished') return this.$t('marketOrdersPage.statusFinished')
      if (status === 3 || statusClass === 'failed') return this.$t('marketOrdersPage.statusFailed')

      return item?.statusText || '--'
    },

    async loadStat(forceRefresh = false) {
      const authKey = this.getAuthKey()

      if (!authKey) {
        this.orderStats = this.createEmptyStat()
        this.statLoaded = true
        this.loadingStat = false
        return
      }

      if (!forceRefresh && statCache.data && statCache.authKey === authKey) {
        this.orderStats = this.normalizeStatData(statCache.data)
        this.statLoaded = true
        return
      }

      if (!forceRefresh && statCache.inflight && statCache.authKey === authKey) {
        this.loadingStat = true
        try {
          const data = await statCache.inflight
          this.orderStats = this.normalizeStatData(data)
          this.statLoaded = true
        } finally {
          this.loadingStat = false
        }
        return
      }

      this.loadingStat = true
      statCache.authKey = authKey

      const request = fetchGeoOrderStat({ silent: true })
        .then((res) => {
          const payload = extractResponseData(res)
          statCache.data = payload
          return payload
        })
        .finally(() => {
          statCache.inflight = null
        })

      statCache.inflight = request

      try {
        const payload = await request
        this.orderStats = this.normalizeStatData(payload)
        this.statLoaded = true
      } finally {
        this.loadingStat = false
      }
    },

    async loadFirstPage(forceRefresh = false) {
      const authKey = this.getAuthKey()

      if (!authKey) {
        this.orders = []
        this.pageNum = 1
        this.total = 0
        this.hasMore = false
        this.loadingList = false
        return
      }

      const cacheKey = this.getPageCacheKey(this.activeFilter, this.pageSize)
      const cacheEntry = pageCacheMap[cacheKey]

      if (!forceRefresh && cacheEntry && cacheEntry.data) {
        const normalized = this.normalizePageData(cacheEntry.data)
        this.orders = normalized.records
        this.pageNum = normalized.current
        this.total = normalized.total
        this.hasMore = this.orders.length < this.total
        return
      }

      if (!forceRefresh && cacheEntry && cacheEntry.inflight) {
        this.loadingList = true
        try {
          const payload = await cacheEntry.inflight
          const normalized = this.normalizePageData(payload)
          this.orders = normalized.records
          this.pageNum = normalized.current
          this.total = normalized.total
          this.hasMore = this.orders.length < this.total
        } finally {
          this.loadingList = false
        }
        return
      }

      this.loadingList = true

      pageCacheMap[cacheKey] = pageCacheMap[cacheKey] || { data: null, inflight: null }

      const params = {
        pageNum: 1,
        pageSize: this.pageSize
      }

      if (this.currentStatusParam !== null && this.currentStatusParam !== undefined) {
        params.status = this.currentStatusParam
      }

      const request = fetchGeoOrderPage(params, { silent: true })
        .then((res) => {
          const payload = extractResponseData(res)
          pageCacheMap[cacheKey].data = payload
          return payload
        })
        .finally(() => {
          if (pageCacheMap[cacheKey]) {
            pageCacheMap[cacheKey].inflight = null
          }
        })

      pageCacheMap[cacheKey].inflight = request

      try {
        const payload = await request
        const normalized = this.normalizePageData(payload)
        this.orders = normalized.records
        this.pageNum = normalized.current
        this.total = normalized.total
        this.hasMore = this.orders.length < this.total
      } finally {
        this.loadingList = false
      }
    },

    async loadMore() {
      if (this.loadingMore || this.loadingList || !this.hasMore || !this.isLoggedIn) return

      this.loadingMore = true
      try {
        const nextPage = this.pageNum + 1
        const params = {
          pageNum: nextPage,
          pageSize: this.pageSize
        }

        if (this.currentStatusParam !== null && this.currentStatusParam !== undefined) {
          params.status = this.currentStatusParam
        }

        const res = await fetchGeoOrderPage(params, { silent: true })
        const payload = extractResponseData(res)
        const normalized = this.normalizePageData(payload)

        const oldMap = {}
        this.orders.forEach((item) => {
          oldMap[item.orderNo] = item
        })

        normalized.records.forEach((item) => {
          oldMap[item.orderNo] = item
        })

        this.orders = Object.values(oldMap)
        this.pageNum = normalized.current || nextPage
        this.total = normalized.total || this.total
        this.hasMore = this.orders.length < this.total
      } catch (e) {
        this.toast(e?.message || this.$t('marketOrdersPage.loadMoreFailed'))
      } finally {
        this.loadingMore = false
      }
    },

    async reloadAll() {
      if (!this.isLoggedIn) {
        this.resetPageState()
        return
      }

      this.errorMsg = ''

      try {
        await Promise.all([
          this.loadStat(true),
          this.loadFirstPage(true)
        ])
      } catch (e) {
        this.errorMsg = e?.message || this.$t('marketOrdersPage.loadDataFailed')
      }
    },

    async changeFilter(filterKey) {
      if (this.activeFilter === filterKey) return
      this.activeFilter = filterKey
      this.closeDetail()

      if (!this.isLoggedIn) return

      try {
        await this.loadFirstPage(false)
      } catch (e) {
        this.errorMsg = e?.message || this.$t('marketOrdersPage.loadListFailed')
      }
    },

    async viewDetail(item) {
      this.detailVisible = true
      this.detailLoading = true
      this.currentOrder = this.normalizeDetailData(item, item)
      document.body.style.overflow = 'hidden'

      try {
        const res = await fetchGeoOrderDetail(item.orderNo, { silent: true })
        const payload = extractResponseData(res)
        this.currentOrder = this.normalizeDetailData(payload, item)
      } catch (e) {
        this.toast(e?.message || this.$t('marketOrdersPage.loadDetailFailed'))
      } finally {
        this.detailLoading = false
      }
    },

    closeDetail() {
      this.detailVisible = false
      this.currentOrder = null
      this.detailLoading = false
      document.body.style.overflow = ''
    },

    formatMoney(val) {
      const num = toSafeNumber(val)
      return num.toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      })
    },

    formatDateTime(date) {
      if (!date) return '--'
      const d = new Date(date)
      if (Number.isNaN(d.getTime())) {
        return String(date).replace('T', ' ').slice(0, 16)
      }
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      const hh = String(d.getHours()).padStart(2, '0')
      const mm = String(d.getMinutes()).padStart(2, '0')
      return `${y}-${m}-${day} ${hh}:${mm}`
    },

    getOrderProgress(item) {
      const progress = toSafeNumber(item?.progress)
      if (progress > 0) return Math.min(100, progress)

      const maxIncome = toSafeNumber(item?.maxIncome)
      const income = toSafeNumber(item?.income)
      if (!maxIncome) return 0
      return Math.min(100, Math.round((income / maxIncome) * 100))
    },

    shortAddress(addr) {
      if (!addr) return '--'
      return `${String(addr).slice(0, 6)}...${String(addr).slice(-4)}`
    },

    shortHash(hash) {
      if (!hash) return '--'
      return `${String(hash).slice(0, 10)}...${String(hash).slice(-8)}`
    },

    copyText(text, successText) {
      if (!text) return

      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(text).then(() => {
          this.toast(successText)
        }).catch(() => {
          this.fallbackCopy(text, successText)
        })
      } else {
        this.fallbackCopy(text, successText)
      }
    },

    fallbackCopy(text, successText) {
      const input = document.createElement('input')
      input.value = text
      document.body.appendChild(input)
      input.select()
      document.execCommand('copy')
      document.body.removeChild(input)
      this.toast(successText)
    },

    copyOrderNo(orderNo) {
      this.copyText(orderNo, this.$t('marketOrdersPage.orderNoCopied'))
    },

    toast(message) {
      if (this.$toast) {
        if (typeof this.$toast === 'function') {
          this.$toast(message)
          return
        }
        if (typeof this.$toast.show === 'function') {
          this.$toast.show(message)
          return
        }
        if (typeof this.$toast.success === 'function') {
          this.$toast.success(message)
          return
        }
      }

      if (this.$message && typeof this.$message.success === 'function') {
        this.$message.success(message)
        return
      }

      alert(message)
    }
  }
}
</script>

<style lang="less" scoped>
.orders-page {
  min-height: 100vh;
  background:
    radial-gradient(circle at top, rgba(88, 136, 255, 0.12) 0%, rgba(88, 136, 255, 0) 34%),
    linear-gradient(180deg, #f3f7ff 0%, #eef4ff 42%, #f8fbff 100%);
  color: #162033;
}

.page-body {
  padding: 12px 12px 28px;
}

.page-heading {
  padding: 8px 2px 2px;
}

.page-title {
  font-size: 30px;
  font-weight: 800;
  color: #142132;
  line-height: 1.2;
}

.page-desc {
  margin-top: 8px;
  font-size: 14px;
  color: #7d8aa0;
  line-height: 1.6;
}

.overview-card {
  position: relative;
  overflow: hidden;
  margin-top: 14px;
  border-radius: 30px;
  padding: 18px 16px 16px;
  background: linear-gradient(135deg, #165fff 0%, #3d84ff 46%, #66abff 100%);
  box-shadow: 0 22px 42px rgba(43, 111, 255, 0.25);
  color: #fff;
}

.overview-bg {
  position: absolute;
  pointer-events: none;
}

.overview-grid {
  inset: 0;
  background-image:
    linear-gradient(rgba(255,255,255,0.045) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,0.045) 1px, transparent 1px);
  background-size: 22px 22px;
}

.overview-orb {
  border-radius: 50%;
  filter: blur(2px);
}

.overview-orb-1 {
  width: 140px;
  height: 140px;
  right: -30px;
  top: -26px;
  background: rgba(255,255,255,0.13);
}

.overview-orb-2 {
  width: 100px;
  height: 100px;
  right: 18px;
  bottom: -24px;
  background: rgba(133, 226, 255, 0.12);
}

.overview-top,
.overview-main,
.overview-bottom {
  position: relative;
  z-index: 2;
}

.overview-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.overview-label {
  font-size: 13px;
  font-weight: 700;
  color: rgba(255,255,255,0.8);
}

.overview-total {
  padding: 7px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
  border: 1px solid rgba(255,255,255,0.2);
  background: rgba(255,255,255,0.12);
  backdrop-filter: blur(10px);
}

.overview-main {
  margin-top: 18px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.overview-main-item {
  padding: 14px 12px;
  border-radius: 18px;
  background: rgba(255,255,255,0.1);
  border: 1px solid rgba(255,255,255,0.14);
  backdrop-filter: blur(10px);
}

.overview-main-item span {
  display: block;
  font-size: 12px;
  color: rgba(255,255,255,0.72);
}

.overview-main-item strong {
  display: block;
  margin-top: 8px;
  font-size: 22px;
  color: #fff;
  line-height: 1.2;
  word-break: break-word;
}

.overview-main-item em {
  display: block;
  margin-top: 6px;
  font-style: normal;
  font-size: 12px;
  color: rgba(255,255,255,0.72);
}

.overview-bottom {
  margin-top: 18px;
}

.overview-progress-label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 13px;
  color: rgba(255,255,255,0.8);
  margin-bottom: 8px;
}

.section-card {
  margin-top: 14px;
  background: rgba(255,255,255,0.86);
  backdrop-filter: blur(16px);
  border-radius: 28px;
  padding: 18px 14px;
  border: 1px solid rgba(180, 205, 255, 0.45);
  box-shadow: 0 14px 34px rgba(104, 132, 182, 0.08);
}

.section-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.section-title {
  font-size: 24px;
  font-weight: 800;
  color: #162235;
  line-height: 1.2;
}

.section-desc {
  margin-top: 8px;
  font-size: 13px;
  color: #7c8aa0;
  line-height: 1.6;
}

.section-badge {
  padding: 8px 12px;
  border-radius: 999px;
  background: linear-gradient(135deg, #edf4ff, #f7faff);
  color: #2b7cff;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}

.section-badge.error {
  color: #ff5f6d;
  background: linear-gradient(135deg, #fff1f3, #fff8f8);
}

.filter-row {
  margin-top: 16px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.filter-chip {
  min-width: 72px;
  height: 38px;
  padding: 0 14px;
  border-radius: 999px;
  background: #edf4ff;
  color: #2b7cff;
  font-size: 13px;
  font-weight: 800;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.filter-chip.active {
  background: linear-gradient(135deg, #2a78ff, #5ca2ff);
  color: #fff;
  box-shadow: 0 10px 24px rgba(55, 123, 255, 0.2);
}

.order-list {
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.order-card {
  padding: 16px 14px;
  border-radius: 24px;
  background: linear-gradient(180deg, #f9fbff 0%, #f4f8ff 100%);
  border: 1px solid #e7efff;
}

.order-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}

.order-no-label {
  font-size: 12px;
  color: #7f8ca2;
}

.order-no {
  margin-top: 6px;
  font-size: 15px;
  font-weight: 800;
  color: #1a2538;
  word-break: break-all;
}

.order-status {
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
  white-space: nowrap;
}

.order-status.running {
  background: #e7f6ef;
  color: #08b57c;
}

.order-status.finished {
  background: #eef3fb;
  color: #75839b;
}

.order-status.pending {
  background: #fff5e7;
  color: #f59e0b;
}

.order-status.failed {
  background: #ffecec;
  color: #ef4444;
}

.order-main-grid {
  margin-top: 14px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.order-main-item {
  padding: 14px 12px;
  border-radius: 18px;
  background: #fff;
  border: 1px solid #e7efff;
}

.order-main-item span {
  display: block;
  font-size: 12px;
  color: #7f8ca2;
}

.order-main-item strong {
  display: block;
  margin-top: 8px;
  font-size: 15px;
  color: #162234;
  line-height: 1.5;
  word-break: break-word;
}

.income-box {
  margin-top: 14px;
  padding: 14px 12px;
  border-radius: 20px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  border: 1px solid #e7efff;
}

.income-box-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.income-box-label {
  font-size: 12px;
  color: #7f8ca2;
}

.income-box-value {
  margin-top: 8px;
  font-size: 24px;
  font-weight: 800;
  color: #152235;
  line-height: 1.2;
}

.income-box-side {
  text-align: right;
}

.income-box-side span {
  display: block;
  font-size: 12px;
  color: #7f8ca2;
}

.income-box-side strong {
  display: block;
  margin-top: 8px;
  font-size: 15px;
  color: #162234;
  line-height: 1.4;
}

.order-progress-row {
  margin-top: 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 12px;
  color: #76859d;
}

.progress-bar {
  margin-top: 8px;
  width: 100%;
  height: 10px;
  border-radius: 999px;
  background: #e7efff;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  border-radius: 999px;
}

.blue-fill {
  background: linear-gradient(90deg, #2b7cff, #60a5ff);
}

.white-fill {
  background: linear-gradient(90deg, rgba(255,255,255,0.98), rgba(255,255,255,0.72));
}

.white-track {
  background: rgba(255,255,255,0.22);
}

.order-actions {
  margin-top: 14px;
  display: flex;
  gap: 10px;
}

.action-btn {
  flex: 1;
  height: 42px;
  border-radius: 14px;
  font-size: 14px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
}

.action-btn.ghost {
  background: #edf4ff;
  color: #2b7cff;
}

.action-btn.primary {
  background: linear-gradient(135deg, #2a78ff, #5ea5ff);
  color: #fff;
  box-shadow: 0 12px 24px rgba(55, 123, 255, 0.18);
}

.empty-box {
  margin-top: 16px;
  padding: 36px 20px;
  border-radius: 22px;
  background: linear-gradient(180deg, #f9fbff 0%, #f4f8ff 100%);
  border: 1px solid #e7efff;
  text-align: center;
}

.empty-title {
  font-size: 18px;
  font-weight: 800;
  color: #182539;
}

.empty-desc {
  margin-top: 10px;
  font-size: 13px;
  line-height: 1.6;
  color: #8694aa;
}

.load-more-wrap {
  margin-top: 4px;
}

.load-more-btn {
  height: 44px;
  border-radius: 14px;
  background: #edf4ff;
  color: #2b7cff;
  font-size: 14px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
}

.load-more-text {
  text-align: center;
  padding: 10px 0 0;
  font-size: 13px;
  color: #7f8ca2;
}

.load-more-text.done {
  color: #a0aec0;
}

.detail-mask {
  position: fixed;
  inset: 0;
  background: rgba(8, 17, 36, 0.42);
  z-index: 90;
  backdrop-filter: blur(4px);
}

.detail-sheet {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 91;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  border-radius: 26px 26px 0 0;
  padding: 10px 14px 20px;
  max-height: 82vh;
  overflow-y: auto;
  box-shadow: 0 -18px 42px rgba(34, 63, 116, 0.18);
}

.detail-sheet-handle {
  width: 52px;
  height: 5px;
  border-radius: 999px;
  background: #d8e4f8;
  margin: 2px auto 12px;
}

.detail-sheet-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.detail-sheet-title {
  font-size: 22px;
  font-weight: 800;
  color: #162235;
  line-height: 1.2;
}

.detail-sheet-desc {
  margin-top: 8px;
  font-size: 13px;
  color: #7c8aa0;
  line-height: 1.6;
}

.detail-close {
  padding: 8px 12px;
  border-radius: 999px;
  background: #edf4ff;
  color: #2b7cff;
  font-size: 13px;
  font-weight: 800;
  white-space: nowrap;
}

.detail-content {
  margin-top: 16px;
}

.detail-status-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}

.detail-order-no span {
  display: block;
  font-size: 12px;
  color: #7f8ca2;
}

.detail-order-no strong {
  display: block;
  margin-top: 6px;
  font-size: 16px;
  color: #1a2538;
  word-break: break-all;
}

.detail-highlight-card {
  margin-top: 14px;
  padding: 16px 14px;
  border-radius: 22px;
  background: linear-gradient(135deg, #2a78ff 0%, #5ea5ff 100%);
  color: #fff;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  box-shadow: 0 16px 32px rgba(55, 123, 255, 0.18);
}

.detail-highlight-main span,
.detail-highlight-side span {
  display: block;
  font-size: 12px;
  color: rgba(255,255,255,0.72);
}

.detail-highlight-main strong {
  display: block;
  margin-top: 8px;
  font-size: 28px;
  line-height: 1.2;
  font-weight: 800;
}

.detail-highlight-side {
  text-align: right;
}

.detail-highlight-side strong {
  display: block;
  margin-top: 8px;
  font-size: 18px;
  font-weight: 800;
}

.detail-grid {
  margin-top: 14px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.detail-item {
  padding: 14px 12px;
  border-radius: 18px;
  background: #f9fbff;
  border: 1px solid #e7efff;
}

.detail-item span {
  display: block;
  font-size: 12px;
  color: #7f8ca2;
}

.detail-item strong {
  display: block;
  margin-top: 8px;
  font-size: 15px;
  color: #162234;
  line-height: 1.5;
  word-break: break-word;
}

.detail-progress-box {
  margin-top: 14px;
  padding: 14px 12px;
  border-radius: 20px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  border: 1px solid #e7efff;
}

.detail-reward-card,
.detail-remark-card {
  margin-top: 14px;
  padding: 14px 12px;
  border-radius: 20px;
  background: linear-gradient(180deg, #f9fbff 0%, #f4f8ff 100%);
  border: 1px solid #e7efff;
}

.detail-reward-title {
  font-size: 15px;
  font-weight: 800;
  color: #182539;
}

.detail-reward-grid {
  margin-top: 14px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.detail-reward-item {
  padding: 14px 12px;
  border-radius: 18px;
  background: #fff;
  border: 1px solid #e7efff;
}

.detail-reward-item span {
  display: block;
  font-size: 12px;
  color: #7f8ca2;
}

.detail-reward-item strong {
  display: block;
  margin-top: 8px;
  font-size: 15px;
  color: #162234;
  line-height: 1.5;
  word-break: break-word;
}

.detail-remark-text {
  margin-top: 10px;
  font-size: 14px;
  line-height: 1.7;
  color: #4a5568;
  white-space: pre-wrap;
  word-break: break-word;
}

.detail-actions {
  margin-top: 16px;
  display: flex;
  gap: 10px;
}

.detail-loading-box {
  margin-top: 16px;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.22s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.sheet-up-enter-active,
.sheet-up-leave-active {
  transition: transform 0.26s ease, opacity 0.26s ease;
}

.sheet-up-enter,
.sheet-up-leave-to {
  transform: translateY(100%);
  opacity: 0.92;
}

@media (max-width: 375px) {
  .overview-main,
  .order-main-grid,
  .detail-grid,
  .detail-reward-grid {
    grid-template-columns: 1fr;
  }

  .income-box-top,
  .detail-highlight-card {
    flex-direction: column;
  }

  .income-box-side,
  .detail-highlight-side {
    text-align: left;
  }
}
</style>